import { Wifi, WifiOff, AlertTriangle, CheckCircle2, Router, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AdminTabShell } from './AdminTabShell';
import {
  useAdminGatewayDiagnostics,
  type GatewayDevice,
  type DataSourceMismatch,
  type GatewayCommand,
  type GatewayDeployment,
} from '@/hooks/admin/useAdminGatewayDiagnostics';

function SimpleTable<T extends Record<string, unknown>>({ rows, columns, emptyText }: {
  rows: T[];
  columns: { key: string; label: string; render?: (row: T) => React.ReactNode }[];
  emptyText?: string;
}) {
  if (rows.length === 0) return <p className="text-xs text-wt-text-muted py-2">{emptyText ?? 'None'}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-wt-text-muted border-b border-wt-border">
          {columns.map(c => (
            <th key={c.key} className="text-left py-1.5 pr-3 font-medium">{c.label}</th>
          ))}
        </tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={(row.id as string) ?? i} className="border-b border-wt-border/50">
              {columns.map(c => (
                <td key={c.key} className="py-1.5 pr-3">
                  {c.render ? c.render(row) : String(row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function opsVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'healthy') return 'default';
  if (status === 'offline') return 'destructive';
  if (status === 'degraded') return 'outline';
  return 'secondary';
}

function lifecycleVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'active') return 'default';
  if (status === 'commissioning') return 'outline';
  if (status === 'revoked' || status === 'decommissioned') return 'destructive';
  return 'secondary';
}

export function GatewayDiagnosticsTab() {
  const { data: d, isLoading, error } = useAdminGatewayDiagnostics();

  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {d && (
        <div className="space-y-4">
          {/* Health summary */}
          {d.summary.offline === 0 && d.summary.stale === 0 && d.data_source_mismatches.length === 0 && d.failed_commands.length === 0 && (
            <div className="flex items-center gap-2 text-xs text-green-500 py-1">
              <CheckCircle2 className="h-4 w-4" />
              All gateway systems healthy — no issues detected.
            </div>
          )}

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardContent className="p-3">
                <p className="text-[10px] text-wt-text-muted">Total Gateways</p>
                <p className="text-lg font-semibold">{d.summary.total}</p>
              </CardContent>
            </Card>
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardContent className="p-3">
                <p className="text-[10px] text-wt-text-muted">Online</p>
                <p className="text-lg font-semibold text-green-500">{d.summary.online}</p>
              </CardContent>
            </Card>
            <Card className={`border-wt-border bg-wt-bg-secondary ${d.summary.offline + d.summary.stale > 0 ? 'border-wt-red-primary/30' : ''}`}>
              <CardContent className="p-3">
                <p className="text-[10px] text-wt-text-muted">Offline / Stale</p>
                <p className={`text-lg font-semibold ${d.summary.offline + d.summary.stale > 0 ? 'text-wt-red-primary' : ''}`}>
                  {d.summary.offline + d.summary.stale}
                </p>
              </CardContent>
            </Card>
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardContent className="p-3">
                <p className="text-[10px] text-wt-text-muted">Unassigned</p>
                <p className="text-lg font-semibold">{d.summary.unassigned}</p>
              </CardContent>
            </Card>
          </div>

          {/* Data source mismatches */}
          {d.data_source_mismatches.length > 0 && (
            <Card className="border-wt-border bg-wt-bg-secondary border-wt-amber-primary/30">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-wt-amber-primary" />
                  Data Source Mismatches ({d.data_source_mismatches.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<DataSourceMismatch>
                  rows={d.data_source_mismatches}
                  columns={[
                    { key: 'property_name', label: 'Property' },
                    { key: 'org_name', label: 'Org' },
                    { key: 'data_source_mode', label: 'Mode', render: r => <span className="font-mono">{r.data_source_mode}</span> },
                    { key: 'has_gateway', label: 'Gateway', render: r => r.has_gateway ? 'Yes' : 'No' },
                    { key: 'mismatch_reason', label: 'Issue', render: r => (
                      <Badge variant="outline" className="text-[10px]">
                        {r.mismatch_reason === 'no_gateway_assigned' ? 'No gateway' : 'Gateway on api_only'}
                      </Badge>
                    )},
                  ]}
                />
              </CardContent>
            </Card>
          )}

          {/* Device fleet table */}
          <Card className="border-wt-border bg-wt-bg-secondary">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Router className="h-4 w-4 text-wt-text-muted" />
                Gateway Devices ({d.devices.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <SimpleTable<GatewayDevice>
                rows={d.devices}
                emptyText="No gateway devices registered"
                columns={[
                  { key: 'claim_code', label: 'Claim Code', render: r => <span className="font-mono">{r.claim_code ?? '—'}</span> },
                  { key: 'property_name', label: 'Property' },
                  { key: 'org_name', label: 'Org' },
                  { key: 'lifecycle_status', label: 'Lifecycle', render: r => (
                    <Badge variant={lifecycleVariant(r.lifecycle_status)} className="text-[10px]">{r.lifecycle_status}</Badge>
                  )},
                  { key: 'operational_status', label: 'Status', render: r => (
                    <Badge variant={opsVariant(r.operational_status)} className="text-[10px]">{r.operational_status}</Badge>
                  )},
                  { key: 'last_heartbeat_at', label: 'Last Heartbeat', render: r => formatRelative(r.last_heartbeat_at) },
                  { key: 'firmware_version', label: 'FW', render: r => <span className="font-mono">{r.firmware_version ?? '—'}</span> },
                  { key: 'wifi_rssi', label: 'RSSI', render: r => r.wifi_rssi != null ? `${r.wifi_rssi} dBm` : '—' },
                  { key: 'bus_devices_responding', label: 'Bus', render: r =>
                    r.bus_devices_expected != null
                      ? <span className={r.bus_devices_responding !== r.bus_devices_expected ? 'text-wt-amber-primary' : ''}>{r.bus_devices_responding}/{r.bus_devices_expected}</span>
                      : '—'
                  },
                ]}
              />
            </CardContent>
          </Card>

          {/* Failed commands */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-wt-red-primary" />
                  Failed Commands — 30d ({d.failed_commands.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<GatewayCommand>
                  rows={d.failed_commands}
                  emptyText="No failed commands in last 30 days"
                  columns={[
                    { key: 'claim_code', label: 'Device', render: r => <span className="font-mono">{r.claim_code ?? r.device_id.slice(0, 8)}</span> },
                    { key: 'command_type', label: 'Command', render: r => <span className="font-mono">{r.command_type}</span> },
                    { key: 'category', label: 'Category' },
                    { key: 'status', label: 'Status', render: r => (
                      <Badge variant="destructive" className="text-[10px]">{r.status}</Badge>
                    )},
                    { key: 'created_at', label: 'Created', render: r => formatDateTime(r.created_at) },
                  ]}
                />
              </CardContent>
            </Card>

            {/* Pending (stuck) commands */}
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <WifiOff className="h-4 w-4 text-wt-amber-primary" />
                  Stuck Pending Commands ({d.pending_commands.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<GatewayCommand>
                  rows={d.pending_commands}
                  emptyText="No stuck pending commands"
                  columns={[
                    { key: 'claim_code', label: 'Device', render: r => <span className="font-mono">{r.claim_code ?? r.device_id.slice(0, 8)}</span> },
                    { key: 'command_type', label: 'Command', render: r => <span className="font-mono">{r.command_type}</span> },
                    { key: 'category', label: 'Category' },
                    { key: 'created_at', label: 'Waiting Since', render: r => formatRelative(r.created_at) },
                  ]}
                />
                {d.pending_commands.length > 0 && (
                  <p className="text-[10px] text-wt-text-muted mt-1.5">Commands pending &gt;5 min. Device may be offline or not polling.</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent OTA deployments */}
          {d.recent_deployments.length > 0 && (
            <Card className="border-wt-border bg-wt-bg-secondary">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Upload className="h-4 w-4 text-wt-text-muted" />
                  Recent OTA Deployments ({d.recent_deployments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <SimpleTable<GatewayDeployment>
                  rows={d.recent_deployments}
                  columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'firmware_version', label: 'Version', render: r => <span className="font-mono">{r.firmware_version ?? '—'}</span> },
                    { key: 'status', label: 'Status', render: r => (
                      <Badge variant={r.status === 'completed' ? 'default' : r.status === 'failed' ? 'destructive' : 'secondary'} className="text-[10px]">
                        {r.status}
                      </Badge>
                    )},
                    { key: 'total_gateways', label: 'Progress', render: r => <>{r.updated_gateways ?? 0}/{r.total_gateways ?? 0}{r.failed_gateways ? ` (${r.failed_gateways} failed)` : ''}</> },
                    { key: 'created_at', label: 'Created', render: r => formatDateTime(r.created_at) },
                  ]}
                />
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </AdminTabShell>
  );
}
